/**
 * Rotation keyframe extraction and post-capture processing.
 *
 * During rotation capture, we collect still frames at target angles.
 * After capture, we run full segmentation on each keyframe to extract
 * precise silhouette widths for Cauchy's formula.
 */
import type { RotationKeyframe, PoseLandmarks } from '@/types/measurements';
import type { SilhouetteObservation } from './cross-section';
import { processPhotoSmart } from './ml-worker-api';
import { measureSilhouetteWidth } from './measurement-engine';

/** Target angles for keyframe capture (every 30 degrees). */
export const ROTATION_TARGETS = [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330];

/** Minimum keyframes needed for useful multi-view measurement. */
export const MIN_KEYFRAMES = 4;

/**
 * Capture a keyframe from a video element at the current rotation angle.
 *
 * @param video - The video element to capture from
 * @param angle - Estimated rotation angle
 * @param landmarks - Current pose landmarks (may be null)
 * @returns A RotationKeyframe with JPEG data URL
 */
export function captureKeyframe(
  video: HTMLVideoElement,
  angle: number,
  landmarks: PoseLandmarks | null
): RotationKeyframe {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(video, 0, 0);
  const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);

  return {
    photoDataUrl,
    angle,
    silhouetteWidths: { chestPx: 0, waistPx: 0, hipPx: 0 },
    landmarks,
    timestamp: Date.now(),
  };
}

/**
 * Run full segmentation on keyframes to extract silhouette widths.
 * This is the "background processing" step after rotation capture completes.
 *
 * @param keyframes - Captured rotation keyframes
 * @param chestY - Normalized Y position of chest (from front photo)
 * @param waistY - Normalized Y position of waist (from front photo)
 * @param hipY - Normalized Y position of hips (from front photo)
 * @param onProgress - Progress callback (0 to 1)
 * @returns Updated keyframes with silhouette widths and observations for Cauchy
 */
export async function processKeyframes(
  keyframes: RotationKeyframe[],
  chestY: number,
  waistY: number,
  hipY: number,
  onProgress?: (progress: number) => void
): Promise<{
  keyframes: RotationKeyframe[];
  chestObs: SilhouetteObservation[];
  waistObs: SilhouetteObservation[];
  hipObs: SilhouetteObservation[];
}> {
  const chestObs: SilhouetteObservation[] = [];
  const waistObs: SilhouetteObservation[] = [];
  const hipObs: SilhouetteObservation[] = [];

  for (let i = 0; i < keyframes.length; i++) {
    const kf = keyframes[i]!;

    try {
      const img = await loadImage(kf.photoDataUrl);
      const detection = await processPhotoSmart(img);

      if (detection.segmentationMask) {
        const mask = detection.segmentationMask;

        const chestWidth = measureSilhouetteWidth(mask, chestY);
        const waistWidth = measureSilhouetteWidth(mask, waistY);
        const hipWidth = measureSilhouetteWidth(mask, hipY);

        kf.silhouetteWidths = {
          chestPx: chestWidth,
          waistPx: waistWidth,
          hipPx: hipWidth,
        };

        // Store landmarks if detection found them
        if (detection.landmarks && detection.landmarks.length > 0) {
          kf.landmarks = detection.landmarks;
        }

        // Add to observation arrays (converting px to cm happens later in the engine)
        if (chestWidth > 0) {
          chestObs.push({ angle: kf.angle, widthCm: chestWidth }); // widthCm is actually px here, converted later
        }
        if (waistWidth > 0) {
          waistObs.push({ angle: kf.angle, widthCm: waistWidth });
        }
        if (hipWidth > 0) {
          hipObs.push({ angle: kf.angle, widthCm: hipWidth });
        }
      }
    } catch {
      // Skip failed keyframes
    }

    onProgress?.((i + 1) / keyframes.length);
  }

  return { keyframes, chestObs, waistObs, hipObs };
}

/**
 * Convert pixel-based observations to cm-based observations.
 */
export function convertObservationsToCm(
  observations: SilhouetteObservation[],
  pixelsPerCm: number
): SilhouetteObservation[] {
  return observations.map((obs) => ({
    angle: obs.angle,
    widthCm: obs.widthCm / pixelsPerCm,
  }));
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load keyframe image'));
    img.src = dataUrl;
  });
}
